import { CreateForm, FormFieldsData } from 'framework/form';
import { useNavigate, useParams } from 'react-router-dom';
import { ShowSection } from 'framework/ui/layout';
import useBillingService, { BillingRequestResponse } from '../../billingService';
import { BillingType, useNewProjectUrl } from 'domain/shared/newProject';
import BillingRequestFormPeriodicPostpaidOrMilestone from '../BillingRequestFormPeriodicPostpaidOrMilestone';
import { mapToBilledItems } from '../../billingUtils';
import { useBillingRouteContext } from '../../BillingRouteContext';
import BillingRequestFormPeriodicPrepaid from '../BillingRequestFormPeriodicPrepaid';
import { CreateBillingRequestFormValues } from '../types';
import { prepareCreateBillingFormData, createCreateBillingBasePayload } from '../utils';

const CreateBillingRequestLayout = () => {
  const { searchBillingScheduleItems, createBillingRequest, getInitiateBillingValidationSchema } = useBillingService();
  const { billingScheduleId, billingItemId } = useParams<Dictionary<string>>();
  const { billingType, projectId } = useBillingRouteContext();
  const { getUpdateBillingRequestUrl, getFinanceUrl } = useNewProjectUrl();
  const navigate = useNavigate();

  const handleGetData = async () => {
    const items = await searchBillingScheduleItems(billingScheduleId!, { itemsIds: [billingItemId!] });
    const billingRequestId = items?.find(item => item.id === billingItemId)?.billingRequestId;
    if (billingRequestId) {
      navigate(
        getUpdateBillingRequestUrl({
          billingScheduleId: billingScheduleId!,
          billingRequestId,
        }),
        { replace: true },
      );
    }

    return prepareCreateBillingFormData(items, billingType);
  };

  const handleSubmit = async (values: FormFieldsData) => {
    const formValues = values as CreateBillingRequestFormValues & { billingType: BillingType };
    const payload = {
      ...createCreateBillingBasePayload(formValues),
      billedItems: mapToBilledItems(formValues.data, formValues.billingType || BillingType.MILESTONE),
    };
    return createBillingRequest(billingScheduleId!, payload);
  };

  return (
    <CreateForm
      getDataFunc={handleGetData}
      submitFormFunc={handleSubmit}
      afterSubmit={{
        redirect: (response: BillingRequestResponse) => {
          const billingRequestId =
            response?.scheduleItems?.find(item => item.id === billingItemId)?.billingRequestId ?? null;

          if (!billingRequestId) {
            return getFinanceUrl(projectId)!;
          }

          return getUpdateBillingRequestUrl({
            billingScheduleId: billingScheduleId!,
            billingRequestId,
          });
        },
        message: 'You are awesome! Billing request has been successfully created.',
      }}
      getValidationSchemaFunc={getInitiateBillingValidationSchema}
    >
      <ShowSection title="Initiate New Billing Request:" titleVariant="h2">
        {billingType === BillingType.PERIODIC_PREPAID ? (
          <BillingRequestFormPeriodicPrepaid />
        ) : (
          <BillingRequestFormPeriodicPostpaidOrMilestone />
        )}
      </ShowSection>
    </CreateForm>
  );
};

export default CreateBillingRequestLayout;
